/**
 * Use Case - Envoyer un message à l'agent RASK
 * Clean Architecture - Application Layer
 * 
 * Pattern aligné avec raskmote-mobile/src/features/conversation/usecases/SendMessageUseCase.ts
 */

import { MessageFactory } from '../../domain/agent/entities/Message';

/**
 * Constantes
 */
const DEFAULT_PLATFORM = 'web';

/**
 * Erreur spécifique au use case de conversation
 */
export class ConversationError extends Error {
  constructor(message, cause = null, context = {}) {
    super(message);
    this.name = 'ConversationError';
    this.cause = cause;
    this.context = context;
  }
}

/**
 * Use case pour envoyer un message à l'agent RASK
 */
export default class SendMessageToAgent {
  /**
   * @param {AgentRepository} agentRepository - Repository pour communiquer avec l'agent
   */
  constructor(agentRepository) {
    if (!agentRepository) {
      throw new Error('Agent repository is required');
    }
    this.agentRepository = agentRepository;
  }

  /**
   * Exécute le use case
   * @param {Object} params - Paramètres d'exécution
   * @param {string} params.message - Message à envoyer
   * @param {Object} params.conversation - Instance de Conversation
   * @param {string} params.authToken - Token Firebase
   * @param {Object} [params.context] - Contexte utilisateur
   * @param {Object} [params.context.location] - Localisation
   * @param {string} [params.context.platform] - Plateforme
   * @param {Object} [params.context.preferences] - Préférences de recherche
   * @param {number} [params.context.timezoneOffset] - Offset timezone en minutes
   * @param {Object} [params.context.output] - Format de sortie
   * @returns {Promise<{userMessage: Object, assistantMessage: Object, conversation: Object}>}
   */
  async execute({ message, conversation, authToken, context = {} }) {
    // Validate inputs
    // RM-149: Allow empty message if uiAction is present
    const hasUiAction = context?.uiAction;
    if (!hasUiAction && (!message || typeof message !== 'string' || !message.trim())) {
      throw new ConversationError('MESSAGE_EMPTY', null, { code: 'MESSAGE_EMPTY' });
    }

    if (!conversation) {
      throw new ConversationError('CONVERSATION_REQUIRED', null, { code: 'CONVERSATION_REQUIRED' });
    }

    if (!authToken) {
      throw new ConversationError('AUTH_REQUIRED', null, { code: 'AUTH_REQUIRED' });
    }

    const trimmedMessage = message ? message.trim() : '';

    // 1. Créer le message utilisateur (seulement si message non vide)
    let userMessage = null;
    if (trimmedMessage) {
      userMessage = MessageFactory.createUserMessage(trimmedMessage);
      // 2. Ajouter à la conversation
      conversation.addMessage(userMessage);
    }
    
    conversation.updateStatus('processing');

    try {
      // 3. Marquer comme envoyé (si message créé)
      if (userMessage) {
        conversation.updateMessageStatus(userMessage.id, 'sent');
      }

      // 4. Préparer le contexte (pas de fallback location implicite)
      const requestContext = {
        ...context,
        platform: context.platform || DEFAULT_PLATFORM,
      };

      // 5. Envoyer au repository avec conversationId pour mémorisation
      const response = await this.agentRepository.sendMessage(
        {
          message: trimmedMessage,
          conversationId: conversation.id, // UUID de la conversation
          context: requestContext,
        },
        authToken
      );

      // 6. Mettre à jour l'ID de conversation si retourné par le backend
      // (le backend peut générer un nouvel UUID si nécessaire)
      if (response.conversationId && response.conversationId !== conversation.id) {
        conversation.id = response.conversationId;
      }

      // 7. Créer la réponse de l'assistant
      const assistantMessage = MessageFactory.createAssistantMessage(
        response.message,
        response.actionCards,
        response.metadata, // Passer TOUTES les métadonnées (y compris toolCallsInfo)
        response.uiResult
      );

      // 8. Ajouter à la conversation
      conversation.addMessage(assistantMessage);
      if (userMessage) {
        conversation.updateMessageStatus(userMessage.id, 'delivered');
      }
      conversation.updateStatus('idle');

      return {
        userMessage,
        assistantMessage,
        conversation,
      };

    } catch (error) {
      // Gérer l'erreur
      if (userMessage) {
        conversation.updateMessageStatus(userMessage.id, 'error');
      }
      conversation.updateStatus('error');

      // Créer un message d'erreur
      const errorMessage = MessageFactory.createErrorMessage(
        error.errorCode || error.message || 'UNKNOWN_ERROR'
      );

      conversation.addMessage(errorMessage);

      throw new ConversationError(
        'MESSAGE_SEND_FAILED',
        error,
        {
          code: 'MESSAGE_SEND_FAILED',
          userMessage,
          errorMessage,
          conversation,
        }
      );
    }
  }
}
